<template>
  <transition name="mc-fade">
    <div
      v-if="value"
      class="mc-overlay"
      @mousedown.self="close"
    >
      <div class="mc-search">
        <gl-icon
          class="mc-search__icon"
          height="16"
          name="pp-search"
          width="16"
        />
        <input
          ref="field"
          v-model="query"
          class="mc-search__field"
          placeholder="Start typing to filter"
          spellcheck="false"
          type="text"
        >
        <button
          v-show="query"
          class="mc-search__clear"
          type="button"
          @click="clearQuery"
        >
          <gl-icon
            height="12"
            name="close"
            width="12"
          />
        </button>
      </div>

      <div class="mc-hint">
        <template v-if="detectedSection">
          Recognised as {{ detected.kind === 'tx' ? 'a transaction hash' : 'an address' }} — pick what to do with it
        </template>
        <template v-else>
          Search by name or synonym — or paste an address to act on it
        </template>
      </div>

      <div
        class="mc-scroll"
        @mousedown.self="close"
      >
        <div class="mc-panel">
          <div
            v-if="!sections.length"
            class="mc-empty"
          >
            Nothing matches “{{ query }}”
          </div>

          <div
            v-else-if="isPalette"
            class="mc-palette"
          >
            <a
              v-for="link in flatLinks"
              :key="link.key"
              class="mc-row"
              :class="{ 'mc-row--focused': link.index === focusIndex }"
              :href="link.href"
              @click.prevent="open(link, $event)"
              @mousemove="focusIndex = link.index"
            >
              <gl-icon
                class="mc-row__icon"
                height="24"
                :name="link.icon"
                width="24"
              />

              <span class="mc-row__title">
                <span
                  v-for="(part, partIndex) in link.parts"
                  :key="partIndex"
                  :class="{ 'mc-tile__match': part.marked }"
                >{{ part.text }}</span>
              </span>

              <span
                v-if="link.counter"
                class="mc-row__counter"
              >
                {{ link.counter > 99 ? '99+' : link.counter }}
              </span>

              <span class="mc-row__section">{{ link.sectionTitle }}</span>
            </a>
          </div>

          <div
            v-for="(section, sectionIndex) in sections"
            v-show="!isPalette"
            :key="section.key"
            class="mc-section"
            :class="{ 'mc-section--recent': section.key === 'recent' }"
            :style="{ '--mc-i': sectionIndex }"
          >
            <div class="mc-section__title">
              {{ section.title }}
            </div>

            <div
              class="mc-grid"
              :class="{ 'mc-grid--detected': section.key === 'detected' }"
            >
              <div
                v-for="link in section.links"
                :key="link.key"
                class="mc-cell"
              >
                <a
                  class="mc-tile"
                  :class="{
                    'mc-tile--active': link.isCurrent,
                    'mc-tile--focused': link.index === focusIndex,
                  }"
                  :href="link.href"
                  @click.prevent="open(link, $event)"
                  @mousemove="focusIndex = link.index"
                >
                  <div class="mc-tile__preview">
                    <gl-icon
                      class="mc-tile__icon"
                      height="36"
                      :name="link.isCurrent && link.activeIcon ? link.activeIcon : link.icon"
                      width="36"
                    />
                    <div
                      v-if="link.counter"
                      class="mc-tile__counter"
                    >
                      {{ link.counter > 99 ? '99+' : link.counter }}
                    </div>
                    <div
                      v-if="link.isCurrent"
                      class="mc-tile__badge"
                    >
                      Current
                    </div>
                    <button
                      v-if="link.create"
                      v-tooltip="{ content: link.create.tooltip, class: 'tooltip--mission-control', placement: 'top' }"
                      class="mc-tile__create"
                      type="button"
                      @click.stop.prevent="openCreate(link)"
                    >
                      <gl-icon
                        height="14"
                        :hover="false"
                        name="add-icon-2"
                        width="14"
                      />
                    </button>
                  </div>

                  <div class="mc-tile__title">
                    <span
                      v-for="(part, partIndex) in link.parts"
                      :key="partIndex"
                      :class="{ 'mc-tile__match': part.marked }"
                    >{{ part.text }}</span>
                  </div>

                  <div
                    v-if="link.subtitle"
                    class="mc-tile__subtitle"
                  >
                    {{ link.subtitle }}
                  </div>
                </a>
              </div>
            </div>

            <div
              v-if="section.chains"
              class="mc-chains"
            >
              <button
                v-for="coin in section.chains"
                :key="coin.key"
                class="mc-chain"
                :class="{ 'mc-chain--active': selectedChain && coin.key === selectedChain.key }"
                type="button"
                @click="selectedChainKey = coin.key"
              >
                <GlBlockchainIcon
                  :blockchain-icon="coin.key"
                  class="mc-chain__icon"
                />
                <span class="mc-chain__label">{{ coin.label }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="mc-hints">
        <span><b>⇧ ⇧</b> toggle</span>
        <span><b>↑ ↓ ← →</b> move</span>
        <span><b>↵</b> open</span>
        <span><b>esc</b> close</span>
      </div>
    </div>
  </transition>
</template>

<script>
// Components
import GlIcon from '@/components/gl-icon'
import GlBlockchainIcon from '@/components/gl-blockchain-icon.vue'
// Mixins
import menuLinksMixin from '@/assets/mixins/menuLinksMixin'
// Vuex
import { mapGetters, mapState } from 'vuex'
// Utils
import { openRouteInNewWindow } from '@/utils/go-to-route'
import { isMatchedByCoin } from '@/utils/helpers'

// The sprite mixes two icon grids: a glyph drawn on a 20-unit canvas inks about
// 15.5px inside a 36px box, one drawn on a 36-unit canvas inks about 28px. Both
// are normalised to the same fraction of their box, so tile and palette icons
// each read as one set: 24px of ink in a 36px icon, 16px in a 24px one.
const ICON_INK_RATIO = 24 / 36

// The last pages actually visited: how many keys are remembered and how many
// tiles of them the RECENT row shows.
const RECENT_STORAGE_KEY = 'missionControlRecentPages'
const RECENT_STORED = 8
const RECENT_SHOWN = 5

// Search synonyms keyed by the menu link's key: people type what they call a
// page, not what the sidebar labels it. Keep this growing alongside the menu.
const LINK_ALIASES = {
  dashboard: ['home', 'overview', 'summary', 'главная', 'дашборд'],
  analytics: ['graph', 'trace', 'tracing', 'visualization', 'investigation', 'граф', 'трейс'],
  autotrace: ['auto', 'automatic', 'robot', 'автотрейс'],
  cases: ['case', 'files', 'investigation', 'кейсы', 'дела'],
  report: ['kyt', 'risk', 'score', 'scoring', 'source of funds', 'sof', 'риск', 'отчет'],
  'kyt-lab': ['lab', 'sandbox', 'playground'],
  monitoring: ['watchlist', 'monitor', 'addresses', 'мониторинг'],
  alerts: ['notifications', 'inbox', 'алерты', 'уведомления'],
  'api-requests': ['log', 'logs', 'requests', 'audit', 'история', 'журнал'],
  entity: ['entities', 'directory', 'database', 'справочник', 'база'],
  counterparty: ['kyb', 'exposure', 'counterparty', 'контрагент'],
  'defi-exposure': ['defi', 'protocol', 'pools', 'дефи'],
  profile: ['account', 'profile', 'me', 'settings', 'профиль'],
  alerting: ['rules', 'triggers', 'notifications', 'правила', 'триггеры'],
  'scoring-profiles': ['tags', 'types', 'profiles', 'теги', 'типы'],
  users: ['users', 'org', 'team', 'members', 'пользователи', 'организации'],
  labeling: ['label', 'tags', 'метки'],
  statistics: ['stats', 'clusters', 'статистика'],
  scoring: ['score', 'scores', 'скоринг'],
  'entity-management': ['entities', 'manager', 'менеджер'],
  'guide-manage': ['guide', 'help', 'manual'],
  integrations: ['api', 'keys', 'webhook', 'интеграции'],
  'user-guide': ['help', 'docs', 'manual', 'справка', 'документация'],
}

// Shorter than this and a query is a page filter, not a pasted address or hash.
const DETECTABLE_LENGTH = 16

// Every EVM chain shares one address shape, so a pasted address usually matches
// several. They are listed under the tile rather than duplicated as tiles.
const MAX_DETECTED_COINS = 4

// Scattered-letter matching only kicks in from this length and must start on a
// word — otherwise "org" drags in Auto Tracing and the grid stops being useful.
const MIN_FUZZY_LENGTH = 4

// The icon sprite is an external file, so a symbol measures 0x0 until <use>
// resolves it. Measuring once on open loses that race on a cold cache.
const ICON_MEASURE_RETRIES = 10
const ICON_MEASURE_DELAY = 60

export default {
  name: 'GlMissionControl',

  components: {
    GlIcon,
    GlBlockchainIcon,
  },

  mixins: [menuLinksMixin],

  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      query: '',
      focusIndex: 0,
      recentKeys: [],
      // Chain picked under the Tracing tile; null means "the first match".
      selectedChainKey: null,
    }
  },

  computed: {
    // Sidebar sections plus the navigable footer links, filtered by the typed
    // query and flattened with a running index so the arrow keys can address
    // every tile by a single number.
    ...mapGetters('analytics', ['allCurrencyList']),
    ...mapState('analytics', ['coinType']),

    // Every link the grid can navigate to, footer included.
    allNavigableLinks() {
      return this.visibleMenuSections
        .reduce((acc, section) => acc.concat(section.links), [])
        .concat(this.visibleFooterLinks.filter(link => link.to))
    },

    // A typed query flips the grid into a vertical palette — a list reads
    // better than tiles once you are searching. A pasted address keeps the
    // action tiles instead.
    isPalette() {
      return Boolean(this.query.trim()) && !this.detectedSection
    },

    // A pasted address or hash is recognised against the manifest's own
    // per-chain patterns, the same ones the rest of the app validates with.
    detected() {
      const value = this.query.trim()
      if (value.length < DETECTABLE_LENGTH) return null

      const coins = this.allCurrencyList || []
      const byAddress = coins.filter(coin => isMatchedByCoin(coins, value, coin.key, 'address'))
      const byTx = coins.filter(coin => isMatchedByCoin(coins, value, coin.key, 'tx'))
      const matched = byAddress.length ? byAddress : byTx

      if (!matched.length) return null

      // The chain already selected in the app is the likeliest target.
      const ordered = matched
        .slice()
        .sort((a, b) => (b.key === this.coinType) - (a.key === this.coinType))

      return {
        kind: byAddress.length ? 'address' : 'tx',
        value,
        coins: ordered.slice(0, MAX_DETECTED_COINS),
      }
    },

    // Which of the matched chains the Tracing action will open.
    selectedChain() {
      if (!this.detected) return null

      return this.detected.coins.find(coin => coin.key === this.selectedChainKey)
        || this.detected.coins[0]
    },

    // What can actually be done with the pasted value, limited to the pages the
    // user is allowed to open. One tile per action: Tracing carries the chain
    // list underneath, while the report resolves the chain on its own.
    detectedSection() {
      if (!this.detected) return null

      const menuLinks = this.visibleMenuSections.reduce((acc, section) => acc.concat(section.links), [])
      const tracing = menuLinks.find(link => link.key === 'analytics')
      const report = menuLinks.find(link => link.key === 'report')
      const param = this.detected.kind === 'tx' ? 'tx' : 'address'
      const links = []

      if (tracing) {
        links.push({
          key: 'detected-tracing',
          title: 'Open in Tracing',
          icon: tracing.icon,
          isAction: true,
          to: {
            name: 'analytics',
            query: {
              [param]: this.detected.value,
              type: this.selectedChain.key,
              essential: Boolean(this.selectedChain.essential),
            },
          },
        })
      }

      if (report) {
        links.push({
          key: 'detected-report',
          title: 'Risk Report',
          icon: report.icon,
          isAction: true,
          to: {
            path: '/report',
            query: { [param]: this.detected.value, type: this.selectedChain.key },
          },
        })
      }

      if (!links.length) return null

      return {
        key: 'detected',
        title: this.detected.kind === 'tx' ? 'TRANSACTION DETECTED' : 'ADDRESS DETECTED',
        chains: this.detected.coins,
        links,
      }
    },

    // Sidebar sections plus the navigable footer links, filtered by the typed
    // query and flattened with a running index so the arrow keys can address
    // every tile by a single number.
    sections() {
      const groups = [
        ...this.visibleMenuSections,
        {
          key: 'more',
          title: 'MORE',
          links: this.visibleFooterLinks.filter(link => link.to),
        },
      ]

      const query = this.query.trim().toLowerCase()
      let index = 0

      const decorate = (link, marks) => ({
        ...link,
        href: this.hrefFor(link),
        isCurrent: link.isAction ? false : this.isCurrent(link),
        parts: this.titleParts(link.title, marks),
        index: index++,
      })

      const result = this.detectedSection
        ? [{
          ...this.detectedSection,
          links: this.detectedSection.links.map(link => decorate(link, [])),
        }]
        : []

      // The last pages actually visited, freshest first — the page just opened
      // from the grid leads the row, wearing its CURRENT badge.
      if (!query && !this.detectedSection && this.recentKeys.length) {
        const links = this.recentKeys
          .map(key => this.allNavigableLinks.find(link => link.key === key))
          .filter(Boolean)
          .slice(0, RECENT_SHOWN)
          .map(link => decorate(link, []))

        if (links.length) {
          result.push({ key: 'recent', title: 'RECENT', links })
        }
      }

      groups.forEach(section => {
        const sectionTitle = (section.title || section.key).toLowerCase()
        const sectionMatched = Boolean(query) && sectionTitle.includes(query)
        const links = []

        section.links.forEach(link => {
          if (!query) {
            links.push(decorate(link, []))
            return
          }

          const match = this.matchLink(link, query, sectionMatched)
          if (match) links.push(decorate(link, match.marks))
        })

        if (links.length) {
          const title = section.title || section.key.toUpperCase()
          links.forEach(item => {
            item.sectionTitle = title
          })
          result.push({ key: section.key, title, links })
        }
      })

      return result
    },

    flatLinks() {
      return this.sections.reduce((acc, section) => acc.concat(section.links), [])
    },
  },

  watch: {
    value(isOpen) {
      if (isOpen) {
        this.query = ''
        this.selectedChainKey = null
        this.$nextTick(() => {
          this.focusCurrent()
          this.normalizeIcons()
          if (this.$refs.field) this.$refs.field.focus()
        })
        window.addEventListener('keydown', this.handleKeydown)
      } else {
        window.removeEventListener('keydown', this.handleKeydown)
      }
    },

    // Navigating from anywhere else (browser back, a link on the page) closes
    // the grid so it never lingers over a page it no longer describes.
    $route() {
      if (this.value) this.close()
      this.recordRecent()
    },

    // Typing narrows the grid — keep the cursor on a tile that still exists.
    query() {
      if (this.focusIndex > this.flatLinks.length - 1) this.focusIndex = 0
      this.selectedChainKey = null
      this.$nextTick(this.normalizeIcons)
    },
  },

  mounted() {
    try {
      this.recentKeys = JSON.parse(localStorage.getItem(RECENT_STORAGE_KEY)) || []
    } catch (e) {
      this.recentKeys = []
    }

    this.recordRecent()
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeydown)
  },

  methods: {
    // Sizes every glyph to the same ink and drops its centre on the centre of
    // the tile, which the sprite's own geometry does not do on its own.
    normalizeIcons(attempt = 0) {
      if (!this.value || !this.$el || !this.$el.querySelectorAll) return

      let unresolved = false

      Array.from(this.$el.querySelectorAll('.mc-tile__icon, .mc-row__icon')).forEach(icon => {
        let box

        try {
          box = icon.getBBox()
        } catch (e) {
          box = null
        }

        // A glyph the sprite has not delivered yet measures nothing; come back
        // for it instead of leaving it at its raw size.
        if (!box || !box.width || !box.height) {
          unresolved = true
          return
        }

        const size = Number(icon.getAttribute('width')) || 0
        if (!size) return

        const scale = (size * ICON_INK_RATIO) / Math.max(box.width, box.height)
        const shiftX = size / 2 - (box.x + box.width / 2) * scale
        const shiftY = size / 2 - (box.y + box.height / 2) * scale

        icon.style.transformOrigin = '0 0'
        icon.style.transform = `translate(${shiftX}px, ${shiftY}px) scale(${scale})`
      })

      if (unresolved && attempt < ICON_MEASURE_RETRIES) {
        setTimeout(() => this.normalizeIcons(attempt + 1), ICON_MEASURE_DELAY)
      }
    },

    // A link matches on its own title, on one of its synonyms, or by belonging
    // to a section whose name was typed. Only title matches get highlighted.
    matchLink(link, query, sectionMatched) {
      const inTitle = this.matchText(link.title, query)
      if (inTitle) return inTitle

      const aliases = LINK_ALIASES[link.key] || []
      if (aliases.some(alias => alias.includes(query))) return { marks: [] }

      return sectionMatched ? { marks: [] } : null
    },

    // Plain substring first; failing that, the letters in order but spread out,
    // so "etdb" still finds Entity Database.
    matchText(text, query) {
      const lower = text.toLowerCase()
      const at = lower.indexOf(query)

      if (at !== -1) {
        return { marks: Array.from({ length: query.length }, (item, i) => at + i) }
      }

      if (query.length < MIN_FUZZY_LENGTH) return null

      const marks = []
      let cursor = 0

      for (let i = 0; i < lower.length && cursor < query.length; i += 1) {
        if (lower[i] === query[cursor]) {
          marks.push(i)
          cursor += 1
        }
      }

      if (cursor !== query.length) return null

      const startsWord = marks[0] === 0 || /[\s-]/.test(lower[marks[0] - 1])

      return startsWord ? { marks } : null
    },

    // Splits the title into runs of matched and unmatched text for the markup.
    titleParts(title, marks) {
      if (!marks.length) return [{ text: title, marked: false }]

      const marked = new Set(marks)
      const parts = []

      for (let i = 0; i < title.length; i += 1) {
        const isMarked = marked.has(i)
        const last = parts[parts.length - 1]

        if (last && last.marked === isMarked) last.text += title[i]
        else parts.push({ text: title[i], marked: isMarked })
      }

      return parts
    },

    // Resolve the current route back to a menu link (the most specific match
    // wins, so /users/42 counts as Organizations) and move it to the front of
    // the remembered list.
    recordRecent() {
      let best = null
      let bestLength = -1

      this.allNavigableLinks.forEach(link => {
        try {
          const { route } = this.$router.resolve(link.to)
          if (route.path === '/') return

          const matches = this.$route.path === route.path
            || this.$route.path.startsWith(`${route.path}/`)

          if (matches && route.path.length > bestLength) {
            best = link
            bestLength = route.path.length
          }
        } catch (e) {
          // an unresolvable link cannot be the current page
        }
      })

      if (!best) return

      this.recentKeys = [best.key, ...this.recentKeys.filter(key => key !== best.key)]
        .slice(0, RECENT_STORED)
      localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(this.recentKeys))
    },

    // The plus on a tile: straight to the create flow, no zoom — a modal or a
    // dedicated form is about to take the screen anyway.
    openCreate(link) {
      this.close()
      this.$router.push(link.create.to).catch(() => {})
    },

    clearQuery() {
      this.query = ''
      if (this.$refs.field) this.$refs.field.focus()
    },

    close() {
      this.$emit('input', false)
    },

    open(link, event) {
      const { isAction, isCurrent, to } = link

      if (isCurrent) {
        this.close()
        return
      }

      // Both search pages read the pasted value only while mounting: Vision
      // additionally ignores it unless it was reached by a full load
      // (analytics/index.vue, the `!from || from.name === 'analytics'` guard),
      // and the report has no $route watcher at all. An in-app push would land
      // on the page without ever running the search, so these open the way the
      // rest of the app opens them — as a fresh document.
      if (isAction) {
        this.close()
        openRouteInNewWindow(to)
        return
      }

      this.zoomIntoTile(link, event && event.currentTarget)
    },

    // The picked card grows to cover the viewport, the route swaps underneath,
    // and the card fades away — the page appears to come out of its own tile.
    zoomIntoTile(link, tileEl) {
      const tile = tileEl || this.$el.querySelectorAll('.mc-tile')[link.index]
      const preview = tile && tile.querySelector('.mc-tile__preview')
      const navigate = () => this.$router.push(link.to).catch(() => {})
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

      if (!preview || reducedMotion || typeof preview.animate !== 'function') {
        this.close()
        navigate()
        return
      }

      const rect = preview.getBoundingClientRect()
      const ghost = preview.cloneNode(true)

      Object.assign(ghost.style, {
        position: 'fixed',
        top: `${rect.top}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        margin: '0',
        zIndex: '9500',
        background: 'var(--white, #fff)',
        transform: 'none',
        pointerEvents: 'none',
      })
      document.body.appendChild(ghost)

      // Whatever happens to the navigation, the ghost must never outlive it.
      const dismiss = setTimeout(() => ghost.remove(), 2500)

      this.close()

      const grow = ghost.animate([
        {
          top: `${rect.top}px`,
          left: `${rect.left}px`,
          width: `${rect.width}px`,
          height: `${rect.height}px`,
          borderRadius: '16px',
        },
        { top: '0px', left: '0px', width: '100vw', height: '100vh', borderRadius: '0px' },
      ], { duration: 380, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' })

      grow.onfinish = async () => {
        await navigate()

        const fade = ghost.animate(
          [{ opacity: 1 }, { opacity: 0 }],
          { duration: 300, easing: 'ease', fill: 'forwards' },
        )

        fade.onfinish = () => {
          clearTimeout(dismiss)
          ghost.remove()
        }
      }
    },

    hrefFor(link) {
      try {
        return this.$router.resolve(link.to).href
      } catch (e) {
        return '#'
      }
    },

    isCurrent(link) {
      try {
        const { route } = this.$router.resolve(link.to)

        return route.path !== '/' && this.$route.path.startsWith(route.path)
      } catch (e) {
        return false
      }
    },

    focusCurrent() {
      const current = this.flatLinks.find(link => link.isCurrent)
      this.focusIndex = current ? current.index : 0
    },

    handleKeydown(event) {
      const { key } = event

      if (key === 'Escape') {
        event.preventDefault()
        this.close()
        return
      }

      if (key === 'Enter') {
        event.preventDefault()
        const link = this.flatLinks[this.focusIndex]
        if (link) this.open(link)
        return
      }

      const directions = {
        ArrowLeft: 'left',
        ArrowRight: 'right',
        ArrowUp: 'up',
        ArrowDown: 'down',
      }

      if (directions[key]) {
        event.preventDefault()
        this.moveFocus(directions[key])
      }
    },

    // Geometric navigation: pick the closest tile in the pressed direction, so
    // arrows behave sanely across sections with different tile counts.
    moveFocus(direction) {
      const tiles = Array.from(this.$el.querySelectorAll('.mc-tile, .mc-row'))
      const current = tiles[this.focusIndex]
      if (!current) return

      const from = current.getBoundingClientRect()
      const fromX = from.left + from.width / 2
      const fromY = from.top + from.height / 2

      let best = -1
      let bestScore = Infinity

      tiles.forEach((tile, index) => {
        if (index === this.focusIndex) return

        const rect = tile.getBoundingClientRect()
        const dx = rect.left + rect.width / 2 - fromX
        const dy = rect.top + rect.height / 2 - fromY

        const forward = { left: -dx, right: dx, up: -dy, down: dy }[direction]
        if (forward <= 1) return

        const sideways = direction === 'left' || direction === 'right' ? Math.abs(dy) : Math.abs(dx)
        const score = forward + sideways * 2

        if (score < bestScore) {
          bestScore = score
          best = index
        }
      })

      if (best !== -1) {
        this.focusIndex = best
        tiles[best].scrollIntoView({ block: 'nearest' })
      }
    },
  },
}
</script>

<style scoped>
.mc-overlay {
  position: fixed;
  inset: 0;
  z-index: 9000;
  display: flex;
  flex-direction: column;
  padding: 28px 24px 20px;
  background: rgba(9, 21, 64, 0.62);
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
}

/* Only the grid scrolls — the search line and the hints stay put. */
.mc-scroll {
  display: flex;
  flex: 1;
  min-height: 0;
  width: 100%;
  justify-content: center;
  overflow-y: auto;
}

.mc-panel {
  width: 100%;
  max-width: 1120px;
  margin: auto;
  /* focus rings are box-shadows — give them room at the clipping edges */
  padding: 8px 8px 2px;
}

.mc-search {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  flex-shrink: 0;
  margin: 0 auto 10px;
  padding: 0 16px;
  max-width: 520px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.22);
  color: var(--white);
  font-size: 14px;
  font-weight: 600;
}

.mc-search__icon {
  flex-shrink: 0;
}

.mc-search__field {
  flex: 1;
  min-width: 0;
  border: none;
  background: none;
  outline: none;
  color: var(--white);
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
}

.mc-search__clear {
  display: flex;
  width: 24px;
  height: 24px;
  margin-right: -6px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s ease;
}

.mc-search__clear:hover {
  background: rgba(255, 255, 255, 0.18);
}

.mc-search__clear .gl-icon {
  min-width: 12px;
  fill: rgba(255, 255, 255, 0.65);
}

.mc-search__clear:hover .gl-icon {
  fill: var(--white);
}

.mc-search__field::placeholder {
  color: rgba(255, 255, 255, 0.55);
  font-weight: 500;
}

.mc-hint {
  flex-shrink: 0;
  margin: 0 auto 20px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 11px;
  letter-spacing: 0.01em;
  text-align: center;
}

.mc-empty {
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
}

.mc-section {
  margin-bottom: 20px;
  animation: mc-rise 0.34s cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: calc(var(--mc-i, 0) * 50ms);
}

/* A thin line under RECENT separates the history from the full grid. */
.mc-section--recent {
  padding-bottom: 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.16);
}

@keyframes mc-rise {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .mc-section {
    animation: none;
  }
}

.mc-section:last-child {
  margin-bottom: 0;
}

.mc-section__title {
  margin-bottom: 10px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.mc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(168px, 1fr));
  /* Wider columns than rows: the hover lift needs room to breathe sideways. */
  gap: 12px 20px;
}

/* Roomier cells so the chain row under an action fits on a single line. */
.mc-grid--detected {
  grid-template-columns: repeat(auto-fill, minmax(212px, 1fr));
}

.mc-cell {
  display: flex;
  flex-direction: column;
}

.mc-tile {
  display: block;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.18s cubic-bezier(0.22, 1, 0.36, 1);
}

.mc-tile__preview {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 84px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid transparent;
  box-shadow: 0 8px 24px rgba(9, 21, 64, 0.25);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

/*
 * 44 of the menu symbols carry fill="#091540" inside the sprite; four of them
 * (Organizations and Integrations plus their -Fill twins) carry no fill at all
 * and would otherwise inherit .gl-icon's own --dark. Pinning that same value
 * keeps the whole set one colour.
 */
.mc-tile__icon {
  display: block;
  fill: var(--Basic-900, #091540);
}

.mc-tile__match {
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;
}

.mc-tile__subtitle {
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-align: center;
  text-transform: uppercase;
}

.mc-tile__title {
  margin-top: 8px;
  text-align: center;
  color: var(--white);
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
}

.mc-tile--focused {
  transform: scale(1.03);
}

.mc-tile--focused .mc-tile__preview {
  box-shadow:
    0 0 0 3px var(--white),
    0 0 0 6px rgba(90, 120, 233, 0.6),
    0 12px 32px rgba(9, 21, 64, 0.45);
}

.mc-tile--active .mc-tile__preview {
  background: var(--white);
  border-color: var(--Brand-Main, #5a78e9);
}


.mc-tile__badge {
  position: absolute;
  left: 8px;
  bottom: 8px;
  padding: 2px 6px;
  border-radius: 6px;
  background: var(--Brand-Main, #5a78e9);
  color: var(--white);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.mc-tile__create {
  position: absolute;
  right: 8px;
  bottom: 8px;
  display: flex;
  width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: var(--Content-Brand, #5a78e9);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.mc-tile__create:hover {
  transform: scale(1.12);
  box-shadow: 0 4px 10px rgba(9, 21, 64, 0.3);
}

.mc-tile__create .gl-icon {
  min-width: 14px;
  fill: var(--white);
}

.mc-tile__counter {
  position: absolute;
  top: 8px;
  right: 8px;
  min-width: 20px;
  padding: 0 5px;
  border-radius: 10px;
  background: var(--Error-Main, #ff4754);
  color: var(--white);
  font-size: 10px;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
}

.mc-chains {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 10px;
}

.mc-chain {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 7px 3px 4px;
  border: 1px solid transparent;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.7);
  font-family: inherit;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.mc-chain:hover {
  background: rgba(255, 255, 255, 0.2);
  color: var(--white);
}

.mc-chain--active {
  border-color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.26);
  color: var(--white);
}

.mc-chain__icon {
  display: flex;
  width: 14px;
  height: 14px;
}

/* The chain icon renders its own svg or bitmap; only a deep rule can size it. */
.mc-chain__icon::v-deep > div {
  display: flex;
}

.mc-chain__icon::v-deep svg,
.mc-chain__icon::v-deep img {
  width: 14px;
  height: 14px;
}

/* Palette mode: a typed query turns the grid into a vertical result list. */
.mc-palette {
  display: flex;
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  flex-direction: column;
  gap: 10px;
  animation: mc-rise 0.25s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.mc-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid transparent;
  box-shadow: 0 4px 14px rgba(9, 21, 64, 0.2);
  color: var(--Basic-900, #091540);
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: box-shadow 0.15s ease;
}

.mc-row--focused {
  box-shadow:
    0 0 0 2px var(--white),
    0 0 0 4px rgba(90, 120, 233, 0.6),
    0 6px 18px rgba(9, 21, 64, 0.3);
}

.mc-row__icon {
  flex-shrink: 0;
  fill: var(--Basic-900, #091540);
}

.mc-row__title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.mc-row__counter {
  min-width: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: var(--Error-Main, #FF4754);
  color: var(--white);
  font-size: 10px;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
}

.mc-row__section {
  flex-shrink: 0;
  color: rgba(9, 21, 64, 0.4);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
}

.mc-hints {
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  gap: 20px;
  padding-top: 14px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 11px;
}

.mc-hints b {
  color: rgba(255, 255, 255, 0.85);
  font-weight: 700;
}

.mc-fade-enter-active,
.mc-fade-leave-active {
  transition: opacity 0.25s ease;
}

.mc-fade-enter-active .mc-panel,
.mc-fade-leave-active .mc-panel {
  transition: transform 0.22s cubic-bezier(0.22, 1, 0.36, 1);
}

.mc-fade-enter,
.mc-fade-leave-to {
  opacity: 0;
}

.mc-fade-enter .mc-panel,
.mc-fade-leave-to .mc-panel {
  transform: scale(0.98);
}
</style>
